import React, { useEffect, useState } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import Alert from 'components_new/atoms/Alert'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import List from 'components_new/atoms/List'
import ListItem from 'components_new/atoms/List/ListItem'
import ListItemText from 'components_new/atoms/List/ListItemText'

import AdvancedDialog from 'components_new/molecules/AdvancedDialog'
import CollapsibleSection from 'components_new/molecules/CollapsibleSection'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import AddAccount from 'components_new/organisms/AddAccount'

import type { TCompanyDto } from '@homepal/api-contract'

import { ParsedCompanyGroup } from 'types/GlobalCompanyGroups'

import IconButton from 'components_new/atoms/IconButton'
import TextField from 'components_new/atoms/TextField'
import { useTranslation } from 'language/translator'
import { formatUsername } from 'utils/functions'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { TAccount } from 'types/GlobalAccount'

interface EditCompanyGroupDialogProps {
  addCompany: (companyId: string, callback?: (err?: boolean) => void) => void
  companiesWithoutCompanyGroup: TCompanyDto[]
  companyGroup: ParsedCompanyGroup | null
  deleteUser: (id: string) => void
  hasIdp: boolean
  integrateCompanyGroups: boolean
  isHomepal?: boolean
  onClose: () => void
  open: boolean
  removeCompany: (companyId: string, callback?: (err?: boolean) => void) => void
  updateCompanyGroup: (
    id: string,
    name?: string,
    economySetId?: string | null,
    callback?: (err?: boolean) => void
  ) => void
}

const EditCompanyGroupDialog = (props: EditCompanyGroupDialogProps) => {
  const {
    addCompany,
    companyGroup,
    deleteUser,
    integrateCompanyGroups,
    removeCompany,
    updateCompanyGroup
  } = props

  const { t } = useTranslation()

  const [addCompanyOpen, setAddCompanyOpen] = useState<boolean>(false)
  const [inviteUserOpen, setInviteUserOpen] = useState<boolean>(false)
  const [deleteUserAlert, setDeleteUserAlert] = useState<TAccount | null>(null)
  const [deleteCompany, setDeleteCompany] = useState<TCompanyDto | null>(null)
  const [addingCompany, setAddingCompany] = useState<string | null>(null)
  const [removingCompany, setRemovingCompany] = useState<boolean>(false)
  const [renameOpen, setRenameOpen] = useState<boolean>(false)
  const [newName, setNewName] = useState<string>(companyGroup?.name || '')
  const [renameLoading, setRenameLoading] = useState<boolean>(false)

  const handleCloseRename = () => {
    setRenameOpen(false)

    setNewName(companyGroup?.name || '')
  }

  useEffect(() => {
    if (props.open) {
      setNewName(companyGroup?.name || '')
    }
  }, [props.open])

  if (!companyGroup) {
    return null
  }

  return (
    <ThemeProvider theme={getTheme('light')}>
      <AdvancedDialog
        actions={
          <Button variant="text" onClick={props.onClose}>
            {t('global', 'close')}
          </Button>
        }
        onClose={props.onClose}
        open={props.open}
        title={t('company_groups', 'edit_company_group')}
      >
        <GenericListItem
          actions={
            integrateCompanyGroups ? null : (
              <IconButton onClick={() => setRenameOpen(true)}>
                <Icon color={'disabled'} fontSize="small" name={'Edit'} />
              </IconButton>
            )
          }
          component="div"
          title={companyGroup.name}
          sx={{ bgcolor: 'grey.200', mb: 2 }}
          variant="company"
        />
        <CollapsibleSection
          actions={
            integrateCompanyGroups ? null : (
              <Button
                onClick={() => setAddCompanyOpen(true)}
                startIcon={<Icon name="AddCircleOutlineOutlined" />}
              >
                {t('global', 'add')}
              </Button>
            )
          }
          size="medium"
          title={t('global', 'companies')}
          variant="blank"
          sx={{ p: 0 }}
        >
          <List>
            {companyGroup.companies
              .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
              .map((company) => (
                <ListItem
                  key={company.id}
                  secondaryAction={
                    integrateCompanyGroups ? null : (
                      <Button
                        onClick={() => setDeleteCompany(company)}
                        variant="text"
                      >
                        {t('company_groups', 'delete_from_company_group')}
                      </Button>
                    )
                  }
                >
                  <Icon color="disabled" name="ApartmentOutlined" />
                  <ListItemText
                    sx={{ ml: 3 }}
                    primary={company.name}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
              ))}
          </List>
        </CollapsibleSection>
        <CollapsibleSection
          actions={
            <Button
              onClick={() => setInviteUserOpen(true)}
              startIcon={<Icon name="AddCircleOutlineOutlined" />}
            >
              {t('global', 'add')}
            </Button>
          }
          size="medium"
          title={t('global', 'users')}
          variant="blank"
          sx={{ p: 0 }}
        >
          <List>
            {[...companyGroup.accounts]
              .sort((a, b) =>
                sortAlphabeticalAsc(formatUsername(a), formatUsername(b))
              )
              .map((user) => (
                <GenericListItem
                  key={user.id}
                  hasHover
                  actions={
                    <>
                      <Button
                        onClick={() => setDeleteUserAlert(user)}
                        variant="text"
                      >
                        {t('global', 'delete')}
                      </Button>
                    </>
                  }
                  body={user.email}
                  title={
                    user.first_name && user.last_name
                      ? user.first_name + ' ' + user.last_name
                      : t('global', 'unnamed')
                  }
                  sx={{ color: 'black', fontSize: 20 }}
                />
              ))}
          </List>
        </CollapsibleSection>
      </AdvancedDialog>
      <SimpleDialog
        actions={
          <Button variant="text" onClick={() => setInviteUserOpen(false)}>
            {t('global', 'close')}
          </Button>
        }
        onClose={() => setInviteUserOpen(false)}
        open={inviteUserOpen}
        title={t('company_groups', 'invite_to_{{name}}', false, {
          name: companyGroup.name
        })}
      >
        <AddAccount hideGroups hideRole companyGroupId={companyGroup.id} />
      </SimpleDialog>
      <SimpleDialog
        title={t('company_groups', 'delete_user_title')}
        open={Boolean(deleteUserAlert)}
        onClose={() => setDeleteUserAlert(null)}
        actions={
          <>
            <Button variant="text" onClick={() => setDeleteUserAlert(null)}>
              {t('global', 'close')}
            </Button>
            <Button
              color="error"
              onClick={() => {
                deleteUser((deleteUserAlert as TAccount).id)

                setDeleteUserAlert(null)
              }}
              variant="contained"
            >
              {t('global', 'delete')}
            </Button>
          </>
        }
        contentText={
          <>
            <code>
              {deleteUserAlert
                ? deleteUserAlert.first_name + ' ' + deleteUserAlert.last_name
                : ''}
            </code>{' '}
            {t('guides', 'delete_confirmation_suffix')}
          </>
        }
      />
      <SimpleDialog
        title={t('company_groups', 'delete_company_from_company_group_title')}
        open={Boolean(deleteCompany)}
        onClose={() => setDeleteCompany(null)}
        actions={
          <>
            <Button variant="text" onClick={() => setDeleteCompany(null)}>
              {t('global', 'close')}
            </Button>
            <Button
              color="error"
              onClick={() => {
                setRemovingCompany(true)
                removeCompany((deleteCompany as TCompanyDto).id, (err) => {
                  if (!err) {
                    setDeleteCompany(null)
                  }
                  setRemovingCompany(false)
                })
              }}
              loading={removingCompany}
              variant="contained"
            >
              {t('global', 'delete')}
            </Button>
          </>
        }
        contentText={t(
          'company_groups',
          'delete_company_from_company_group_body_{{companyGroupName}}_{{companyName}}',
          false,
          {
            companyGroupName: companyGroup?.name ?? '',
            companyName: deleteCompany?.name ?? ''
          }
        )}
      />
      <SimpleDialog
        actions={
          <Button variant="text" onClick={() => setAddCompanyOpen(false)}>
            {t('global', 'close')}
          </Button>
        }
        onClose={() => setAddCompanyOpen(false)}
        open={addCompanyOpen}
        title={t('company_groups', 'add_company')}
      >
        {props.companiesWithoutCompanyGroup.length > 0 ? (
          <List sx={{ position: 'relative' }}>
            {props.companiesWithoutCompanyGroup.map((company) => (
              <ListItem
                key={company.id}
                secondaryAction={
                  <Button
                    loading={company.id === addingCompany}
                    disabled={
                      Boolean(addingCompany) && company.id !== addingCompany
                    }
                    onClick={() => {
                      setAddingCompany(company.id)

                      addCompany(company.id, () => setAddingCompany(null))
                    }}
                    variant="text"
                  >
                    {t('global', 'add')}
                  </Button>
                }
              >
                <Icon color="disabled" name="ApartmentOutlined" />
                <ListItemText
                  sx={{ ml: 3 }}
                  primary={company.name}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Alert severity="warning" icon={<Icon name="Warning" />}>
            {t('company_groups', 'no_companies_without_company_group')}
          </Alert>
        )}
      </SimpleDialog>
      <SimpleDialog
        actions={
          <Button variant="text" onClick={handleCloseRename}>
            {t('global', 'close')}
          </Button>
        }
        onClose={handleCloseRename}
        open={renameOpen}
        title={t('company_groups', 'rename_{{name}}', false, {
          name: companyGroup.name
        })}
      >
        <TextField
          fullWidth={true}
          label={t('global', 'title')}
          autoComplete="off"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
          required
        />
        <Button
          disabled={newName.length === 0}
          loading={renameLoading}
          fullWidth
          variant="contained"
          size="large"
          onClick={() => {
            updateCompanyGroup(companyGroup.id, newName, undefined, (err) => {
              if (!err) {
                setRenameOpen(false)
              }

              setRenameLoading(false)
            })

            setRenameLoading(true)
          }}
        >
          {t('global', 'save')}
        </Button>
      </SimpleDialog>
    </ThemeProvider>
  )
}

export default EditCompanyGroupDialog
